import React, { useState, useEffect } from 'react';

const ICDSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [codes, setCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const pageSize = 20;

  // Fetch all codes on initial load
  useEffect(() => {
    fetchCodes(searchTerm);
  }, [currentPage]);

  const fetchCodes = async (term = '') => {
    setIsLoading(true);
    setError(null);

    try {
      const searchParam = term ? `&term=${encodeURIComponent(term)}` : '';
      const response = await fetch(`/codes?page=${currentPage}&per_page=${pageSize}${searchParam}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch ICD codes');
      }

      const data = await response.json();
      setCodes(data);
      
      // Get total count from headers or calculate based on response
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0', 10) || data.length;
      setTotalResults(totalCount);
      setTotalPages(Math.ceil(totalCount / pageSize));
    } catch (err) {
      setError(err.message);
      setCodes([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchCodes(searchTerm);
  };

  const handleClear = () => {
    setSearchTerm('');
    setCurrentPage(1);
    fetchCodes('');
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  // Generate array of page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if there are few
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show a window of pages around current page
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
      
      // Adjust if we're near the end
      if (endPage - startPage < maxPagesToShow - 1) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }
      
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) pages.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="icd-search-container">
      <h2 className="mb-4">ICD Codes</h2>
      
      <div className="card mb-4">
        <div className="card-body">
          <form onSubmit={handleSearch} className="mb-0">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search by code or description (e.g., F32.1, depression)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search for ICD codes"
              />
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span>
                    <i className="fas fa-spinner fa-spin me-1"></i> Searching...
                  </span>
                ) : (
                  <span>
                    <i className="fas fa-search me-1"></i> Search
                  </span>
                )}
              </button>
              {searchTerm && (
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={handleClear}
                >
                  <i className="fas fa-times me-1"></i> Clear
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-1"></i> {error}
        </div>
      )}

      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center bg-dark">
          <h5 className="mb-0 text-white">
            {searchTerm ? 'Search Results' : 'All ICD Codes'}
            {totalResults > 0 && <span className="badge bg-primary ms-2">{totalResults}</span>}
          </h5>
          {!isLoading && codes.length > 0 && (
            <div className="text-white">
              Showing {(currentPage - 1) * pageSize + 1} - {Math.min(currentPage * pageSize, totalResults)} of {totalResults}
            </div>
          )}
        </div>
        
        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="thead-dark">
              <tr>
                <th style={{width: "15%"}}>Code</th>
                <th style={{width: "65%"}}>Description</th>
                <th style={{width: "20%"}}>Category</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="3" className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-2">Loading codes...</p>
                  </td>
                </tr>
              ) : codes.length > 0 ? (
                codes.map((code) => (
                  <tr key={code.id}>
                    <td>
                      <span className="badge bg-info p-2">{code.name}</span>
                    </td>
                    <td>{code.long_description}</td>
                    <td>
                      <span className="badge bg-secondary">{code.group}</span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="text-center py-4">
                    <i className="fas fa-info-circle text-info me-1"></i>
                    {searchTerm ? 'No codes matching your search criteria.' : 'No codes available.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {!isLoading && totalPages > 1 && (
          <div className="card-footer bg-dark">
            <nav aria-label="ICD codes pagination">
              <ul className="pagination justify-content-center mb-0">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-angle-double-left"></i>
                  </button>
                </li>
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="fas fa-angle-left"></i>
                  </button>
                </li>
                
                {getPageNumbers().map((page, index) => (
                  <li 
                    key={index} 
                    className={`page-item ${page === currentPage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}
                  >
                    <button 
                      className="page-link high-contrast"
                      onClick={() => page !== '...' && handlePageChange(page)}
                      disabled={page === '...'}
                      style={page === currentPage ? { backgroundColor: '#3498db', color: 'white', fontWeight: 'bold' } : {}}
                    >
                      {page}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="fas fa-angle-right"></i>
                  </button>
                </li>
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button 
                    className="page-link" 
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="fas fa-angle-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </div>
    </div>
  );
};

export default ICDSearch; 
